/**
 * "Which days do you usually teach?" — SPEC §11.3, §2.1.
 *
 * The single most valuable screen in the product: an instructor who answers it once never
 * has to paint a calendar again, and the nine instructors with no teaching history (§4.4)
 * have no other way to tell us anything.
 *
 * ## The shape of the question
 *
 * Days first, details second. Tapping a day is the whole interaction for somebody with a
 * simple schedule; frequency, time and place only appear once a day is chosen, and each
 * defaults to the commonest answer. A form that asked for seven fields per day up front
 * would be abandoned on a phone.
 *
 * ## A day can hold more than one session
 *
 * Donald teaches a morning class and an afternoon class at the same site on the same day.
 * The first version of this screen allowed exactly one slot per weekday, so the only way to
 * say that was the free-text note — which is invisible to the coverage grid, meaning a
 * second class nobody knew about.
 *
 * The database was always shaped for it: `AvailabilityDate` is unique on
 * `(instructor, date, location, period)`, deliberately allowing two slots on one day at
 * different times. This screen just refused to ask.
 *
 * ## Week parity is never asked about
 *
 * "Every other Saturday" needs a reference week (§2.1), and no instructor can reliably say
 * which week is week one. The client deliberately sends no `anchorDate`; the server picks
 * the next occurrence of that weekday. Getting it wrong shifts every date by seven days and
 * looks like the app inventing a schedule.
 */

import type { Frequency, LocationOption, PatternSlotInput, Weekday } from '../lib/api.js';

const DAYS: { key: Weekday; short: string; long: string }[] = [
  { key: 'SUNDAY', short: 'Sun', long: 'Sunday' },
  { key: 'MONDAY', short: 'Mon', long: 'Monday' },
  { key: 'TUESDAY', short: 'Tue', long: 'Tuesday' },
  { key: 'WEDNESDAY', short: 'Wed', long: 'Wednesday' },
  { key: 'THURSDAY', short: 'Thu', long: 'Thursday' },
  { key: 'FRIDAY', short: 'Fri', long: 'Friday' },
  { key: 'SATURDAY', short: 'Sat', long: 'Saturday' },
];

const FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: 'EVERY', label: 'Every week' },
  { value: 'ALTERNATING', label: 'Every other week' },
  { value: 'FIRST', label: '1st of the month' },
  { value: 'SECOND', label: '2nd of the month' },
  { value: 'THIRD', label: '3rd of the month' },
  { value: 'FOURTH', label: '4th of the month' },
  { value: 'LAST', label: 'Last of the month' },
];

/** Half-hourly, 6am to 8pm. Outside that is a phone call, not a dropdown. */
const TIMES = Array.from({ length: 29 }, (_, i) => {
  const minutes = 6 * 60 + i * 30;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const label = `${hour12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
  return { value, label };
});

const timeLabel = (value?: string) =>
  value ? (TIMES.find((t) => t.value === value)?.label ?? value) : null;

let keyCounter = 0;
const nextKey = () => `slot-${keyCounter++}`;

export interface PatternDraft extends PatternSlotInput {
  /** Stable key for React across re-orders; never sent. */
  key: string;
}

export interface PatternBuilderProps {
  slots: PatternDraft[];
  locations: LocationOption[];
  disabled?: boolean;
  onChange: (slots: PatternDraft[]) => void;
}

/** Sort by weekday, then by time within the day, so a day reads morning-first. */
function ordered(slots: PatternDraft[]): PatternDraft[] {
  return [...slots].sort(
    (a, b) =>
      DAYS.findIndex((d) => d.key === a.weekday) - DAYS.findIndex((d) => d.key === b.weekday) ||
      (a.startTime ?? '').localeCompare(b.startTime ?? ''),
  );
}

/**
 * Slots that clash — same day, same frequency, same place, same time.
 *
 * The server rejects these (they would be one calendar day and two rows, doubling every
 * count that reads the pattern), but it should never get that far: "Saturday twice with no
 * time on either" is an easy thing to do by accident when adding a second session, and the
 * fix is obvious if we say so on the spot.
 */
export function duplicateKeys(slots: PatternDraft[]): Set<string> {
  const counts = new Map<string, string[]>();
  for (const s of slots) {
    const id = `${s.weekday}|${s.frequency}|${s.locationId}|${s.startTime ?? ''}`;
    counts.set(id, [...(counts.get(id) ?? []), s.key]);
  }
  const clashing = new Set<string>();
  for (const keys of counts.values()) {
    if (keys.length > 1) keys.forEach((k) => clashing.add(k));
  }
  return clashing;
}

export function PatternBuilder({ slots, locations, disabled, onChange }: PatternBuilderProps) {
  const home = locations.find((l) => l.isHome) ?? locations[0];
  const clashing = duplicateKeys(slots);

  const daysOn = DAYS.filter((d) => slots.some((s) => s.weekday === d.key));

  function makeSlot(day: Weekday, startTime?: string): PatternDraft {
    return {
      key: nextKey(),
      weekday: day,
      frequency: 'EVERY',
      locationId: home?.id ?? 0,
      ...(startTime ? { startTime } : {}),
    };
  }

  function toggleDay(day: Weekday) {
    if (slots.some((s) => s.weekday === day)) {
      onChange(slots.filter((s) => s.weekday !== day));
    } else {
      // Defaults chosen so the commonest schedule needs no further taps: every week, at the
      // home site, no specific time. A time is offered but never demanded — "Saturdays" is a
      // real answer and forcing a time would make people invent one.
      onChange(ordered([...slots, makeSlot(day)]));
    }
  }

  function addSession(day: Weekday) {
    const existing = slots.filter((s) => s.weekday === day);
    const last = existing[existing.length - 1];
    // Seed the new session with a later time than the one before it, so adding a second
    // session is never an instant duplicate of the first.
    const seedIndex = last?.startTime
      ? Math.min(TIMES.findIndex((t) => t.value === last.startTime) + 8, TIMES.length - 1)
      : TIMES.findIndex((t) => t.value === '13:00');
    const seed = TIMES[seedIndex]?.value;
    const copy = makeSlot(day, seed);
    if (last) {
      copy.frequency = last.frequency;
      copy.locationId = last.locationId;
    }
    onChange(ordered([...slots, copy]));
  }

  function update(key: string, patch: Partial<PatternSlotInput>) {
    onChange(ordered(slots.map((s) => (s.key === key ? { ...s, ...patch } : s))));
  }

  function removeSlot(key: string) {
    onChange(slots.filter((s) => s.key !== key));
  }

  return (
    <div className="stack">
      <div className="daypicker" role="group" aria-label="Days you usually teach">
        {DAYS.map((d) => {
          const on = slots.some((s) => s.weekday === d.key);
          return (
            <button
              key={d.key}
              type="button"
              className={`daypicker__day${on ? ' daypicker__day--on' : ''}`}
              aria-pressed={on}
              aria-label={d.long}
              disabled={disabled}
              onClick={() => toggleDay(d.key)}
            >
              {d.short}
            </button>
          );
        })}
      </div>

      {slots.length === 0 && (
        <p className="small muted" style={{ margin: 0 }}>
          Tap the days you normally teach. You can change this at any time.
        </p>
      )}

      {daysOn.map((day) => {
        const sessions = ordered(slots).filter((s) => s.weekday === day.key);
        return (
          <div key={day.key} className="card--inset stack" style={{ gap: 12 }}>
            <div className="row row--between">
              <strong>{day.long}</strong>
              {/* Only when the day holds a single class. With two, this sat about 12px above
                  the first session's own ×, both right-aligned — two touch targets a thumb
                  cannot tell apart, where the top one silently removes the WHOLE day and the
                  one below removes a single class. Each session carries its own × instead,
                  and removing the last one removes the day. One control, one meaning. */}
              {sessions.length === 1 && (
                <button
                  type="button"
                  className="daylist__remove"
                  aria-label={`Remove ${day.long}`}
                  disabled={disabled}
                  onClick={() => toggleDay(day.key)}
                >
                  ×
                </button>
              )}
            </div>

            {sessions.map((slot, index) => (
              <div key={slot.key} className={index > 0 ? 'session session--extra' : 'session'}>
                {sessions.length > 1 && (
                  <div className="row row--between">
                    <span className="session__label">
                      {timeLabel(slot.startTime) ?? `Session ${index + 1}`}
                    </span>
                    <button
                      type="button"
                      className="daylist__remove"
                      aria-label={
                        timeLabel(slot.startTime)
                          ? `Remove the ${timeLabel(slot.startTime)} class on ${day.long}`
                          : `Remove class ${index + 1} on ${day.long}`
                      }
                      disabled={disabled}
                      onClick={() => removeSlot(slot.key)}
                    >
                      ×
                    </button>
                  </div>
                )}

                <label className="field">
                  <span className="field__label">How often</span>
                  <select
                    className="select"
                    value={slot.frequency}
                    disabled={disabled}
                    onChange={(e) => update(slot.key, { frequency: e.target.value as Frequency })}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span className="field__label">Start time (optional)</span>
                  <select
                    className="select"
                    value={slot.startTime ?? ''}
                    disabled={disabled}
                    onChange={(e) =>
                      update(slot.key, {
                        ...(e.target.value
                          ? { startTime: e.target.value }
                          : { startTime: undefined, period: 'ALL_DAY' }),
                      })
                    }
                  >
                    <option value="">No particular time</option>
                    {TIMES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Hidden entirely for the twenty-odd instructors who only ever work one
                    site — a select with one option is a question that reads as a decision. */}
                {locations.length > 1 && (
                  <label className="field">
                    <span className="field__label">Where</span>
                    <select
                      className="select"
                      value={slot.locationId}
                      disabled={disabled}
                      onChange={(e) => update(slot.key, { locationId: Number(e.target.value) })}
                    >
                      {locations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.canonicalName}
                          {l.isHome ? ' (home)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {slot.frequency === 'ALTERNATING' && (
                  <p className="small muted" style={{ margin: 0 }}>
                    We'll start from your next {day.long} and go every other week from there.
                    Tell us if that lands on the wrong week and we'll shift it.
                  </p>
                )}

                {clashing.has(slot.key) && (
                  <p className="small error-text" style={{ margin: 0 }} role="alert">
                    This is the same day, time and place as another entry — give them
                    different times.
                  </p>
                )}
              </div>
            ))}

            {/* Donald teaches a morning class and an afternoon class at the same site. */}
            <button
              type="button"
              className="btn btn--ghost btn--block"
              style={{ minHeight: 40 }}
              disabled={disabled}
              onClick={() => addSession(day.key)}
            >
              + Add another class on {day.long}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Strip the React keys before sending. */
export function toSlotInput(slots: PatternDraft[]): PatternSlotInput[] {
  return slots.map(({ key: _key, ...slot }) => slot);
}

/** `Every Saturday at 10 AM` — for the summary line. */
export function describeSlot(slot: PatternDraft): string {
  const day = DAYS.find((d) => d.key === slot.weekday)?.long ?? slot.weekday;
  const freq = FREQUENCIES.find((f) => f.value === slot.frequency)?.label ?? '';
  const time = slot.startTime ? ` at ${timeLabel(slot.startTime)}` : '';
  const prefix =
    slot.frequency === 'EVERY'
      ? `Every ${day}`
      : slot.frequency === 'ALTERNATING'
        ? `Every other ${day}`
        : `${freq.replace(' of the month', '')} ${day}`;
  return `${prefix}${time}`;
}
